<template>
  <div class="article-editor-page">
    <div ref="editorTopbar" class="editor-topbar">
      <div class="topbar-left">
        <el-button type="text" class="back-button" icon="el-icon-arrow-left" @click="handleBack">
          返回
        </el-button>
        <div class="editor-brand">
          <div class="brand-title">{{ isEdit ? '编辑文章' : '写文章' }}</div>
          <div class="brand-subtitle">
            {{ saveStatusText }}
            <span v-if="lastSavedLabel" class="save-time">{{ lastSavedLabel }}</span>
          </div>
        </div>
      </div>

      <div class="topbar-center">
        <div class="editor-meta-pill">
          <span>{{ wordCount }} 字</span>
          <span>{{ estimatedReadingTime }} 分钟阅读</span>
          <span>{{ article.category || '其他' }}</span>
        </div>
      </div>

      <div class="topbar-right">
        <el-button size="small" @click="createNewArticle">
          新建
        </el-button>
        <el-button size="small" @click="saveDraft" :loading="saving">
          保存草稿
        </el-button>
        <el-button type="primary" size="small" @click="showPublishDialog" :loading="publishing">
          发布
        </el-button>
      </div>
    </div>

    <div class="editor-shell">
      <main ref="editorWorkspace" class="editor-workspace">
        <div
          v-if="toolbarFixed"
          class="workspace-header-placeholder"
          :style="{ height: `${toolbarHeight}px` }"
        ></div>
        <div
          ref="workspaceHeader"
          class="workspace-header"
          :class="{ 'is-fixed': toolbarFixed }"
          :style="workspaceHeaderStyle"
        >
          <div class="sticky-toolbar-card">
            <div class="mode-switch">
              <button
                class="mode-switch-btn"
                :class="{ active: editorMode === 'edit' }"
                @click="editorMode = 'edit'"
              >
                编辑
              </button>
              <button
                class="mode-switch-btn"
                :class="{ active: editorMode === 'preview' }"
                @click="editorMode = 'preview'"
              >
                预览
              </button>
            </div>

            <div class="toolbar-wrap" v-if="editorMode === 'edit'">
              <div class="edit-toolbar">
                <div class="toolbar-follow-tip">正文工具栏</div>
                <div class="toolbar-divider"></div>
              <div class="toolbar-group">
                <button class="toolbar-btn toolbar-btn-text" :disabled="!canUndo" @click="undoEditor" title="撤销">
                  撤
                </button>
                <button class="toolbar-btn toolbar-btn-text" :disabled="!canRedo" @click="redoEditor" title="重做">
                  重
                </button>
              </div>

              <div class="toolbar-group">
                <button class="toolbar-btn" data-action="bold" @click="formatText('bold')" title="粗体">
                  <strong>B</strong>
                </button>
                <button class="toolbar-btn" data-action="italic" @click="formatText('italic')" title="斜体">
                  <em>I</em>
                </button>
                <button class="toolbar-btn" data-action="underline" @click="formatText('underline')" title="下划线">
                  <u>U</u>
                </button>
              </div>

              <div class="toolbar-group">
                <button class="toolbar-btn" data-action="align" data-value="left" @click="formatText('align', 'left')" title="左对齐">
                  <i class="el-icon-arrow-left"></i>
                </button>
                <button class="toolbar-btn" data-action="align" data-value="center" @click="formatText('align', 'center')" title="居中">
                  <i class="el-icon-s-unfold"></i>
                </button>
                <button class="toolbar-btn" data-action="align" data-value="right" @click="formatText('align', 'right')" title="右对齐">
                  <i class="el-icon-arrow-right"></i>
                </button>
              </div>

              <div class="toolbar-group">
                <button class="toolbar-btn" data-action="list" data-value="bullet" @click="formatText('list', 'bullet')" title="无序列表">
                  <span class="toolbar-text">•</span>
                </button>
                <button class="toolbar-btn" data-action="list" data-value="ordered" @click="formatText('list', 'ordered')" title="有序列表">
                  <span class="toolbar-text">1.</span>
                </button>
              </div>

              <div class="toolbar-group">
                <button class="toolbar-btn" @click="handleImageUpload" title="插入图片">
                  <i class="el-icon-picture"></i>
                </button>
                <button class="toolbar-btn" @click="insertLink" title="插入链接">
                  <i class="el-icon-paperclip"></i>
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>

        <div class="workspace-body">
          <div v-if="editorMode === 'edit'" class="editor-card">
            <div class="editor-card-header">
              <div class="header-label">正文编辑</div>
              <div class="header-hint">支持粘贴图片、拖拽封面、快速排版</div>
            </div>

            <div class="input-block title-block">
              <el-input
                v-model="article.title"
                placeholder="输入标题，清晰表达你的观点"
                class="title-input"
                maxlength="64"
                show-word-limit
              />
            </div>

            <div class="input-block subtitle-block">
              <el-input
                v-model="article.subtitle"
                placeholder="一句副标题，补充标题信息（选填）"
                class="subtitle-input"
                maxlength="120"
                show-word-limit
              />
            </div>

            <div class="content-section">
              <div class="section-label">正文</div>
              <quill-editor
                ref="editor"
                v-model="article.content"
                :options="editorOptions"
                class="content-editor"
                @change="onEditorChange"
              />
            </div>

            <div class="input-block summary-block">
              <div class="section-label">摘要</div>
              <el-input
                v-model="article.summary"
                type="textarea"
                :rows="4"
                placeholder="用 1-2 段话概括文章核心内容，便于在列表页和分享场景展示"
                maxlength="200"
                show-word-limit
                class="summary-input"
              />
            </div>
          </div>

          <div v-else class="preview-card">
            <div v-if="hasContent" class="preview-article">
              <div class="preview-cover" v-if="article.coverImage">
                <img :src="article.coverImage" alt="封面">
              </div>
              <div class="preview-body">
                <div class="preview-category">{{ article.category || '其他' }}</div>
                <h1 class="preview-title">{{ article.title || '请输入标题' }}</h1>
                <p v-if="article.subtitle" class="preview-subtitle">{{ article.subtitle }}</p>
                <div class="preview-author-row">
                  <span>{{ article.author || 'CarDesignSpace 编辑部' }}</span>
                  <span>{{ new Date().toLocaleDateString() }}</span>
                  <span>{{ estimatedReadingTime }} 分钟阅读</span>
                </div>
                <p v-if="article.summary" class="preview-summary">{{ article.summary }}</p>
                <div class="preview-content ql-snow">
                  <div class="ql-editor" v-html="article.content"></div>
                </div>
              </div>
            </div>
            <div v-else class="preview-empty">
              <i class="el-icon-document"></i>
              <p>还没有可预览的内容</p>
              <span>先写下标题或正文，右侧会实时同步发布信息</span>
            </div>
          </div>
        </div>

        <button
          v-if="editorMode === 'edit'"
          class="floating-image-btn"
          type="button"
          @click="handleImageUpload"
        >
          <i class="el-icon-picture"></i>
          <span>插入图片</span>
        </button>
      </main>

      <aside class="editor-sidebar">
        <div class="sidebar-card publish-card">
          <div class="sidebar-title">发布设置</div>
          <div class="sidebar-desc">像知乎一样，把正文和发布配置拆开，写作时更专注。</div>

          <div class="sidebar-section">
            <div class="field-label">封面</div>
            <div class="cover-upload" v-if="!article.coverImage">
              <el-upload
                :action="uploadUrl"
                :headers="uploadHeaders"
                :show-file-list="false"
                :on-success="handleCoverSuccess"
                :on-error="handleUploadError"
                :before-upload="beforeCoverUpload"
                accept="image/*"
                drag
              >
                <i class="el-icon-upload"></i>
                <div class="upload-text">
                  <p>上传文章封面</p>
                  <p class="upload-hint">建议横图，列表展示更好看</p>
                </div>
              </el-upload>
            </div>

            <div class="cover-preview sidebar-cover-preview" v-else>
              <img :src="article.coverImage" alt="封面图片">
              <div class="cover-overlay">
                <el-button type="text" icon="el-icon-edit" @click="changeCover">更换</el-button>
                <el-button type="text" icon="el-icon-delete" @click="removeCover">删除</el-button>
              </div>
            </div>
          </div>

          <div class="sidebar-section">
            <div class="field-label">作者名</div>
            <el-input
              v-model="article.author"
              placeholder="展示作者名（选填）"
              maxlength="20"
            />
          </div>

          <div class="sidebar-section">
            <div class="field-label">分类</div>
            <el-select v-model="article.category" placeholder="选择文章分类" style="width: 100%">
              <el-option label="新车发布" value="新车发布"></el-option>
              <el-option label="试驾评测" value="试驾评测"></el-option>
              <el-option label="行业资讯" value="行业资讯"></el-option>
              <el-option label="技术解析" value="技术解析"></el-option>
              <el-option label="汽车文化" value="汽车文化"></el-option>
              <el-option label="改装案例" value="改装案例"></el-option>
              <el-option label="购车指南" value="购车指南"></el-option>
              <el-option label="维修保养" value="维修保养"></el-option>
              <el-option label="政策法规" value="政策法规"></el-option>
              <el-option label="其他" value="其他"></el-option>
            </el-select>
          </div>

          <div class="sidebar-section">
            <div class="field-label">标签</div>
            <div class="tag-editor">
              <el-tag
                v-for="tag in article.tags"
                :key="tag"
                closable
                @close="removeArticleTag(tag)"
                class="tag-item"
              >
                {{ tag }}
              </el-tag>
              <el-input
                v-if="articleTagInputVisible"
                ref="articleTagInput"
                v-model="articleTagInputValue"
                size="small"
                class="tag-input"
                @keyup.enter.native="addArticleTag"
                @blur="addArticleTag"
              />
              <el-button v-else size="small" plain @click="showArticleTagInput">+ 添加标签</el-button>
            </div>
          </div>

          <div class="sidebar-section sidebar-checks">
            <el-checkbox v-model="article.featured">设为推荐文章</el-checkbox>
          </div>

          <div class="sidebar-actions">
            <el-button plain @click="showHistory">历史版本</el-button>
            <el-button type="primary" @click="showPublishDialog">发布文章</el-button>
          </div>
        </div>

        <div class="sidebar-card stats-card">
          <div class="sidebar-title">写作概览</div>
          <div class="stats-grid">
            <div class="stat-box">
              <div class="stat-value">{{ wordCount }}</div>
              <div class="stat-label">总字数</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">{{ estimatedReadingTime }}</div>
              <div class="stat-label">预计阅读</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">{{ article.tags.length }}</div>
              <div class="stat-label">标签数</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">{{ article.coverImage ? '已传' : '未传' }}</div>
              <div class="stat-label">封面状态</div>
            </div>
          </div>

          <div class="quality-checks">
            <div class="check-item" :class="{ done: !!article.title.trim() }">
              <i :class="article.title.trim() ? 'el-icon-success' : 'el-icon-more'"></i>
              <span>标题明确</span>
            </div>
            <div class="check-item" :class="{ done: !!plainTextContent.trim() }">
              <i :class="plainTextContent.trim() ? 'el-icon-success' : 'el-icon-more'"></i>
              <span>正文已填写</span>
            </div>
            <div class="check-item" :class="{ done: !!article.summary.trim() }">
              <i :class="article.summary.trim() ? 'el-icon-success' : 'el-icon-more'"></i>
              <span>摘要已完善</span>
            </div>
            <div class="check-item" :class="{ done: !!article.coverImage }">
              <i :class="article.coverImage ? 'el-icon-success' : 'el-icon-more'"></i>
              <span>封面更吸引点击</span>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <!-- 图片调整工具栏 -->
    <div class="image-toolbar" v-if="showImageToolbar" @click.stop>
      <div class="image-toolbar-content">
        <div class="image-toolbar-header">
          <span>图片调整</span>
          <i class="el-icon-close" @click="hideImageToolbar"></i>
        </div>
        <div class="image-toolbar-body">
          <div class="image-mode-options">
            <div class="mode-option" :class="{ active: selectedImageMode === 'adaptive' }" @click="setImageDisplayMode('adaptive')">
              <div class="mode-option-icon mode-adaptive-icon"></div>
              <span>自适应</span>
            </div>
            <div class="mode-option" :class="{ active: selectedImageMode === 'fixed' }" @click="setImageDisplayMode('fixed')">
              <div class="mode-option-icon mode-fixed-icon"></div>
              <span>取消自适应</span>
            </div>
          </div>
          <div class="image-size-options">
            <div class="size-option" @click="setImageSize('small')" :class="{ active: selectedImageSize === 'small', disabled: selectedImageMode === 'adaptive' }">
              <div class="size-preview small"></div>
              <span>小图</span>
            </div>
            <div class="size-option" @click="setImageSize('medium')" :class="{ active: selectedImageSize === 'medium', disabled: selectedImageMode === 'adaptive' }">
              <div class="size-preview medium"></div>
              <span>中图</span>
            </div>
            <div class="size-option" @click="setImageSize('large')" :class="{ active: selectedImageSize === 'large', disabled: selectedImageMode === 'adaptive' }">
              <div class="size-preview large"></div>
              <span>大图</span>
            </div>
            <div class="size-option" @click="setImageSize('full')" :class="{ active: selectedImageSize === 'full', disabled: selectedImageMode === 'adaptive' }">
              <div class="size-preview full"></div>
              <span>全宽</span>
            </div>
          </div>
          <div class="image-actions">
            <el-button size="small" @click="openBodyImageCropDialog">裁剪图片</el-button>
            <el-button size="small" @click="deleteSelectedImage">删除图片</el-button>
            <el-button size="small" @click="hideImageToolbar">完成</el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 发布对话框 -->
    <el-dialog
      title="发布文章"
      :visible.sync="publishDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="publish-form">
        <el-form :model="publishForm" label-width="80px">
          <el-form-item label="分类">
            <el-select v-model="publishForm.category" placeholder="选择文章分类" style="width: 100%">
              <el-option label="新车发布" value="新车发布"></el-option>
              <el-option label="试驾评测" value="试驾评测"></el-option>
              <el-option label="行业资讯" value="行业资讯"></el-option>
              <el-option label="技术解析" value="技术解析"></el-option>
              <el-option label="汽车文化" value="汽车文化"></el-option>
              <el-option label="改装案例" value="改装案例"></el-option>
              <el-option label="购车指南" value="购车指南"></el-option>
              <el-option label="维修保养" value="维修保养"></el-option>
              <el-option label="政策法规" value="政策法规"></el-option>
              <el-option label="其他" value="其他"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="标签">
            <el-tag
              v-for="tag in publishForm.tags"
              :key="tag"
              closable
              @close="removeTag(tag)"
              class="tag-item"
            >
              {{ tag }}
            </el-tag>
            <el-input
              v-if="tagInputVisible"
              ref="tagInput"
              v-model="tagInputValue"
              size="small"
              @keyup.enter.native="addTag"
              @blur="addTag"
              class="tag-input"
            />
            <el-button v-else size="small" @click="showTagInput">+ 添加标签</el-button>
          </el-form-item>

          <el-form-item label="设置">
            <el-checkbox v-model="publishForm.featured">推荐文章</el-checkbox>
          </el-form-item>
        </el-form>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="publishDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="publishArticle" :loading="publishing">
          发布文章
        </el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="裁剪封面"
      :visible.sync="coverCropDialogVisible"
      width="820px"
      :close-on-click-modal="false"
      @close="closeCoverCropDialog"
    >
      <div class="cover-cropper-dialog">
        <div class="cover-cropper-stage">
          <div
            ref="coverCropFrame"
            class="cover-cropper-frame"
            @mousedown="startCoverDrag"
          >
            <img
              v-if="coverCropImageUrl"
              :src="coverCropImageUrl"
              :style="coverCropImageStyle"
              class="cover-cropper-image"
              draggable="false"
            >
            <div class="cover-cropper-mask"></div>
          </div>
        </div>

        <div class="cover-cropper-controls">
          <div class="cropper-control-row">
            <span>缩放</span>
            <el-slider
              v-model="coverCropScale"
              :min="coverCropMinScale"
              :max="coverCropMaxScale"
              :step="0.01"
              :show-tooltip="false"
              @input="handleCoverScaleInput"
            />
          </div>
          <div class="cropper-control-actions">
            <el-button size="small" @click="resetCoverCrop">重置</el-button>
            <el-button size="small" @click="centerCoverCrop">居中</el-button>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="closeCoverCropDialog">取消</el-button>
        <el-button type="primary" :loading="uploadingCover" @click="uploadCroppedCover">
          裁剪并上传
        </el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="裁剪正文图片"
      :visible.sync="bodyImageCropDialogVisible"
      width="820px"
      :close-on-click-modal="false"
      @close="closeBodyImageCropDialog"
    >
      <div class="cover-cropper-dialog">
        <div class="cover-cropper-stage">
          <div
            ref="bodyImageCropFrame"
            class="cover-cropper-frame body-image-crop-frame"
            @mousedown="startBodyImageDrag"
          >
            <img
              v-if="bodyImageCropImageUrl"
              :src="bodyImageCropImageUrl"
              :style="bodyImageCropImageStyle"
              class="cover-cropper-image"
              draggable="false"
            >
            <div class="cover-cropper-mask"></div>
          </div>
        </div>

        <div class="cover-cropper-controls">
          <div class="cropper-control-row">
            <span>缩放</span>
            <el-slider
              v-model="bodyImageCropScale"
              :min="bodyImageCropMinScale"
              :max="bodyImageCropMaxScale"
              :step="0.01"
              :show-tooltip="false"
              @input="handleBodyImageScaleInput"
            />
          </div>
          <div class="cropper-control-actions">
            <el-button size="small" @click="resetBodyImageCrop">重置</el-button>
            <el-button size="small" @click="centerBodyImageCrop">居中</el-button>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="closeBodyImageCropDialog">取消</el-button>
        <el-button type="primary" :loading="uploadingBodyImageCrop" @click="applyBodyImageCrop">
          裁剪并替换
        </el-button>
      </div>
    </el-dialog>

    <input
      ref="coverFileInput"
      type="file"
      accept="image/*"
      style="display: none"
      @change="handleCoverFileChange"
    >
  </div>
</template>

<script>
import { apiClient } from '@/services/api'
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'



export default {
  name: 'ArticleEditor',
  components: {
    quillEditor
  },
  data() {
    return {
      article: {
        title: '',
        subtitle: '',
        author: '',
        content: '',
        summary: '',
        coverImage: '',
        category: '其他',
        tags: [],
        featured: false,
        status: 'draft'
      },
      publishForm: {
        category: '其他',
        tags: [],
        featured: false
      },
      publishDialogVisible: false,
      tagInputVisible: false,
      tagInputValue: '',
      articleTagInputVisible: false,
      articleTagInputValue: '',
      saving: false,
      publishing: false,
      uploadingCover: false,
      uploadingBodyImageCrop: false,
      isEdit: false,
      articleId: null,
      editorMode: 'edit',
      saveState: 'idle',
      lastSavedAt: null,
      toolbarFixed: false,
      toolbarLeft: 0,
      toolbarWidth: 0,
      toolbarTop: 88,
      toolbarHeight: 0,
      toolbarThresholdTop: 0,
      coverCropDialogVisible: false,
      coverCropImageUrl: '',
      coverCropFile: null,
      coverCropNaturalWidth: 0,
      coverCropNaturalHeight: 0,
      coverCropScale: 1,
      coverCropLastScale: 1,
      coverCropMinScale: 1,
      coverCropMaxScale: 3,
      coverCropX: 0,
      coverCropY: 0,
      coverCropDragging: false,
      coverCropDragStartX: 0,
      coverCropDragStartY: 0,
      coverCropOriginX: 0,
      coverCropOriginY: 0,
      showImageToolbar: false,
      selectedImage: null,
      selectedImageSize: 'medium',
      selectedImageMode: 'fixed',
      bodyImageCropTarget: null,
      bodyImageCropDialogVisible: false,
      bodyImageCropImageUrl: '',
      bodyImageCropNaturalWidth: 0,
      bodyImageCropNaturalHeight: 0,
      bodyImageCropScale: 1,
      bodyImageCropLastScale: 1,
      bodyImageCropMinScale: 1,
      bodyImageCropMaxScale: 3,
      bodyImageCropX: 0,
      bodyImageCropY: 0,
      bodyImageCropDragging: false,
      bodyImageCropDragStartX: 0,
      bodyImageCropDragStartY: 0,
      bodyImageCropOriginX: 0,
      bodyImageCropOriginY: 0,
      canUndo: false,
      canRedo: false,
      editorOptions: {
        theme: 'snow',
        placeholder: '从这里开始写正文...',
        modules: {
          toolbar: false,
          history: {
            delay: 300,
            maxStack: 500,
            userOnly: true
          }
        }
      }
    }
  },
  computed: {
    hasContent() {
      return !!(this.article.title && this.article.title.trim()) || 
             !!(this.article.content && this.article.content.trim()) || 
             !!(this.article.summary && this.article.summary.trim())
    },
    hasRichMediaContent() {
      return /<(img|video|iframe|table|blockquote|pre|ul|ol)\b/i.test(this.article.content || '')
    },
    canPersistArticle() {
      return !!(this.article.title && this.article.title.trim()) &&
        (!!this.plainTextContent || this.hasRichMediaContent)
    },
    plainTextContent() {
      const html = this.article.content || ''
      return html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    wordCount() {
      const titleCount = (this.article.title || '').trim().length
      const summaryCount = (this.article.summary || '').trim().length
      return titleCount + summaryCount + this.plainTextContent.length
    },
    estimatedReadingTime() {
      return Math.max(1, Math.ceil(this.wordCount / 400))
    },
    saveStatusText() {
      if (this.publishing) return '发布中...'
      if (this.saving) return '保存中...'
      if (this.saveState === 'saved') return '已保存'
      if (this.saveState === 'error') return '保存失败'
      return this.isEdit ? '正在编辑' : '未发布'
    },
    lastSavedLabel() {
      if (!this.lastSavedAt) return ''
      return `最近保存于 ${this.formatDateTime(this.lastSavedAt)}`
    },
    workspaceHeaderStyle() {
      if (!this.toolbarFixed) return {}
      return {
        left: `${this.toolbarLeft}px`,
        top: `${this.toolbarTop}px`,
        width: `${this.toolbarWidth}px`
      }
    },
    coverCropImageStyle() {
      return {
        width: `${this.coverCropNaturalWidth * this.coverCropScale}px`,
        height: `${this.coverCropNaturalHeight * this.coverCropScale}px`,
        left: `${this.coverCropX}px`,
        top: `${this.coverCropY}px`
      }
    },
    bodyImageCropImageStyle() {
      return {
        width: `${this.bodyImageCropNaturalWidth * this.bodyImageCropScale}px`,
        height: `${this.bodyImageCropNaturalHeight * this.bodyImageCropScale}px`,
        left: `${this.bodyImageCropX}px`,
        top: `${this.bodyImageCropY}px`
      }
    },
    uploadUrl() {
      // 在生产环境使用相对路径，开发环境使用完整URL
      if (process.env.NODE_ENV === 'production') {
        return '/api/upload/cover'
      }
      return `${process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000'}/api/upload/cover`
    },
    uploadHeaders() {
      const token = localStorage.getItem('token')
      return token ? { Authorization: `Bearer ${token}` } : {}
    }
  },
  mounted() {
    console.log('🚀 ArticleEditor 组件已挂载')
    console.log('🔍 路由参数:', this.$route.params)
    
    // 配置 Quill 编辑器
    this.$nextTick(() => {
      console.log('🔄 开始配置 Quill 编辑器')
      if (this.$refs.editor) {
        console.log('✅ 编辑器引用存在')
        const editor = this.$refs.editor.quill
        console.log('✅ Quill 实例已创建')
        
        // 增强的粘贴处理
        editor.root.addEventListener('paste', (event) => {
          this.handleEnhancedPaste(event, editor)
        })
        
        // 初始化工具栏状态
        this.updateToolbarState()
        this.updateHistoryState()
        
        // 初始化图片点击事件
        this.initImageClickEvents()
        
        // 检查是否是编辑模式，在编辑器初始化后加载文章
        if (this.$route.params.id) {
          console.log('📝 检测到编辑模式，文章ID:', this.$route.params.id)
          this.isEdit = true
          this.articleId = this.$route.params.id
          console.log('🔄 开始加载文章...')
          this.loadArticle()
        } else {
          console.log('📝 新建文章模式')
        }
      } else {
        console.error('❌ 编辑器引用不存在')
      }
    })
    
    // 定期自动保存
    this.autoSaveInterval = setInterval(() => {
      if (this.hasContent && !this.saving) {
        this.autoSave()
      }
    }, 30000) // 每30秒自动保存

    this.$nextTick(() => {
      this.measureToolbar()
      window.addEventListener('scroll', this.handleWindowScroll, { passive: true })
      window.addEventListener('resize', this.handleWindowResize, { passive: true })
    })
  },
  beforeDestroy() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval)
    }
    window.removeEventListener('scroll', this.handleWindowScroll)
    window.removeEventListener('resize', this.handleWindowResize)
    document.removeEventListener('mousemove', this.handleCoverDrag)
    document.removeEventListener('mouseup', this.endCoverDrag)
    document.removeEventListener('mousemove', this.handleBodyImageDrag)
    document.removeEventListener('mouseup', this.endBodyImageDrag)
    if (this.coverCropImageUrl) {
      URL.revokeObjectURL(this.coverCropImageUrl)
    }
  },
  watch: {
    article: {
      deep: true,
      handler() {
        if (!this.saving && !this.publishing && this.saveState === 'saved') {
          this.saveState = 'idle'
        }
      }
    },
    editorMode() {
      this.$nextTick(() => {
        this.measureToolbar()
      })
    }
  },
  methods: {
    handleWindowScroll() {
      this.updateToolbarStateOnScroll()
    },

    handleWindowResize() {
      this.measureToolbar()
    },

    getToolbarTopOffset() {
      const topbarHeight = this.$refs.editorTopbar
        ? this.$refs.editorTopbar.getBoundingClientRect().height
        : 72
      return topbarHeight + 16
    },

    measureToolbar() {
      this.$nextTick(() => {
        const header = this.$refs.workspaceHeader
        const workspace = this.$refs.editorWorkspace
        if (!header || !workspace) return

        const workspaceRect = workspace.getBoundingClientRect()
        const topOffset = this.getToolbarTopOffset()

        this.toolbarTop = topOffset
        this.toolbarLeft = workspaceRect.left
        this.toolbarWidth = workspaceRect.width
        this.toolbarHeight = header.offsetHeight

        if (!this.toolbarFixed) {
          this.toolbarThresholdTop = window.scrollY + header.getBoundingClientRect().top
        }

        this.updateToolbarStateOnScroll()
      })
    },

    updateToolbarStateOnScroll() {
      const workspace = this.$refs.editorWorkspace
      const header = this.$refs.workspaceHeader
      if (!workspace || !header) return

      const topOffset = this.getToolbarTopOffset()
      const workspaceRect = workspace.getBoundingClientRect()
      const shouldFix = window.scrollY >= (this.toolbarThresholdTop - topOffset)

      this.toolbarFixed = shouldFix && workspaceRect.bottom > (topOffset + header.offsetHeight)
      this.toolbarTop = topOffset
      this.toolbarLeft = workspaceRect.left
      this.toolbarWidth = workspaceRect.width
      this.toolbarHeight = header.offsetHeight
    },

    async loadArticle() {
      try {
        console.log('🔄 开始加载文章，ID:', this.articleId)
        const response = await apiClient.get(`/articles/${this.articleId}`)
        console.log('📡 API响应:', response)
        
        // 检查响应结构
        let article
        if (response.status === 'success' && response.data && response.data.article) {
          // 标准格式: {status: 'success', data: {article: {...}}}
          article = response.data.article
        } else if (response.article) {
          // 直接格式: {article: {...}, userLiked: false}
          article = response.article
        } else {
          throw new Error('无法解析文章数据')
        }
        
        console.log('📄 文章数据:', article)
        
          this.article = {
            title: article.title || '',
          subtitle: article.subtitle || '',
          author: article.author || '',
            content: article.content || '',
            summary: article.summary || '',
            coverImage: article.coverImage || '',
            category: article.category || '其他',
            tags: article.tags || [],
            featured: article.featured || false,
          status: article.status || 'published' // 保持原有状态，默认为已发布
          }
        this.lastSavedAt = article.updatedAt || new Date()
        this.saveState = 'saved'
        
        console.log('✅ 文章数据已加载到组件:', this.article)
          
          // 初始化发布表单
          this.publishForm = {
            category: this.article.category,
            tags: [...this.article.tags],
          featured: this.article.featured
        }
        
        // 将内容设置到编辑器中
        console.log('🖊️ 准备设置编辑器内容...')
        this.setEditorContent()
        
        // 如果第一次设置失败，延迟后尝试备用方法
        setTimeout(() => {
          if (!this.$refs.editor.quill.getText().trim()) {
            console.log('🔄 第一次设置可能失败，尝试备用方法...')
            this.setEditorContentDirect()
          }
        }, 1000)
      } catch (error) {
        console.error('❌ 加载文章失败:', error)
        this.$message.error('加载文章失败')
        this.handleBack()
      }
    },

    // 设置编辑器内容的方法
    setEditorContent() {
      console.log('🖊️ setEditorContent 被调用')
      console.log('📄 当前文章内容:', this.article.content)
      console.log('🔍 编辑器引用状态:', {
        editor: !!this.$refs.editor,
        quill: !!(this.$refs.editor && this.$refs.editor.quill)
      })
      
      this.$nextTick(() => {
        if (this.$refs.editor && this.$refs.editor.quill) {
          console.log('🖊️ 编辑器已准备就绪，开始设置内容')
          if (this.article.content) {
            try {
              console.log('📝 使用 setContents 设置内容...')
              // 使用 Quill 的 setContents 方法设置内容
              this.$refs.editor.quill.setContents(this.$refs.editor.quill.clipboard.convert(this.article.content))
              console.log('✅ 编辑器内容设置成功')
              this.updateHistoryState()
              
              // 为所有图片添加默认尺寸类（如果没有的话）
              this.$nextTick(() => {
                const images = this.$refs.editor.quill.root.querySelectorAll('img')
                images.forEach(img => {
                  if (!img.classList.contains('image-small') && 
                      !img.classList.contains('image-medium') && 
                      !img.classList.contains('image-large') && 
                      !img.classList.contains('image-full') &&
                      !img.classList.contains('image-adaptive')) {
                    img.classList.add('image-adaptive')
                  }
                })
              })
            } catch (error) {
              console.error('❌ 设置编辑器内容失败:', error)
              // 如果 setContents 失败，尝试使用 setText
              console.log('📝 尝试使用 setText 设置内容...')
              this.$refs.editor.quill.setText(this.article.content)
              this.updateHistoryState()
            }
          } else {
            console.warn('⚠️ 文章内容为空')
          }
        } else {
          console.warn('⚠️ 编辑器引用不存在，等待编辑器初始化...')
          // 如果编辑器还没初始化，延迟重试
          setTimeout(() => {
            console.log('🔄 重试设置编辑器内容...')
            this.setEditorContent()
          }, 500)
        }
      })
    },

    // 备用方法：直接设置 HTML 内容
    setEditorContentDirect() {
      console.log('🖊️ 使用直接方法设置编辑器内容')
      this.$nextTick(() => {
        if (this.$refs.editor && this.$refs.editor.quill) {
          if (this.article.content) {
            try {
              // 直接设置 HTML 内容
              this.$refs.editor.quill.root.innerHTML = this.article.content
              console.log('✅ 直接设置内容成功')
              this.updateHistoryState()
            } catch (error) {
              console.error('❌ 直接设置内容失败:', error)
            }
          }
        }
      })
    },

    // 显示图片调整工具栏
    openImageToolbar(imageElement) {
      this.selectedImage = imageElement
      this.showImageToolbar = true
      
      // 获取当前图片的尺寸类
      const currentSize = this.getImageSizeClass(imageElement)
      this.selectedImageSize = currentSize
      this.selectedImageMode = this.getImageDisplayMode(imageElement)
      
      // 定位工具栏
      this.positionImageToolbar(imageElement)
    },

    // 隐藏图片调整工具栏
    hideImageToolbar() {
      this.showImageToolbar = false
      this.selectedImage = null
    },

    // 设置图片尺寸
    setImageSize(size) {
      if (!this.selectedImage) return
      if (this.selectedImageMode === 'adaptive') return
      
      console.log('🖼️ 设置图片尺寸:', size)
      this.selectedImageSize = size
      
      // 保存当前光标位置
      const editor = this.$refs.editor.quill
      const range = editor.getSelection()
      
      // 移除所有尺寸类
      this.selectedImage.classList.remove('image-small', 'image-medium', 'image-large', 'image-full')
      this.selectedImage.classList.remove('image-adaptive')
      
      // 添加新的尺寸类
      this.selectedImage.classList.add(`image-${size}`)
      
      console.log('✅ 图片尺寸类已设置:', this.selectedImage.className)
      
      // 不更新 article.content，避免触发编辑器重新渲染
      // 只在需要保存时才更新内容
      
      // 恢复光标位置
      if (range) {
        this.$nextTick(() => {
          try {
            editor.setSelection(range.index, range.length)
            console.log('✅ 光标位置已恢复')
          } catch (error) {
            console.log('❌ 恢复光标位置失败:', error)
          }
        })
      }
    },

    setImageDisplayMode(mode) {
      if (!this.selectedImage) return

      this.selectedImageMode = mode
      this.selectedImage.classList.remove('image-small', 'image-medium', 'image-large', 'image-full')
      this.selectedImage.classList.remove('image-adaptive')

      if (mode === 'adaptive') {
        this.selectedImage.classList.add('image-adaptive')
      } else {
        this.selectedImage.classList.add(`image-${this.selectedImageSize || 'medium'}`)
      }

      this.updateEditorContent()
    },

    // 删除选中的图片
    deleteSelectedImage() {
      if (!this.selectedImage) return
      
      // 保存当前光标位置
      const editor = this.$refs.editor.quill
      const range = editor.getSelection()
      
      this.selectedImage.remove()
      this.hideImageToolbar()
      
      // 不更新 article.content，避免触发编辑器重新渲染
      // 只在需要保存时才更新内容
      
      // 恢复光标位置
      if (range) {
        this.$nextTick(() => {
          try {
            editor.setSelection(range.index, range.length)
          } catch (error) {
            console.log('恢复光标位置失败:', error)
          }
        })
      }
    },

    // 获取图片的尺寸类
    getImageSizeClass(imageElement) {
      if (imageElement.classList.contains('image-small')) return 'small'
      if (imageElement.classList.contains('image-medium')) return 'medium'
      if (imageElement.classList.contains('image-large')) return 'large'
      if (imageElement.classList.contains('image-full')) return 'full'
      return 'medium' // 默认中等尺寸
    },

    getImageDisplayMode(imageElement) {
      return imageElement.classList.contains('image-adaptive') ? 'adaptive' : 'fixed'
    },



    // 定位图片工具栏
    positionImageToolbar(imageElement) {
      this.$nextTick(() => {
        const toolbar = document.querySelector('.image-toolbar')
        if (!toolbar || !imageElement) return
        
        const imageRect = imageElement.getBoundingClientRect()
        const toolbarRect = toolbar.getBoundingClientRect()
        
        // 计算工具栏位置
        let left = imageRect.left + (imageRect.width / 2) - (toolbarRect.width / 2)
        let top = imageRect.bottom + 10
        
        // 确保工具栏不超出视窗
        if (left < 10) left = 10
        if (left + toolbarRect.width > window.innerWidth - 10) {
          left = window.innerWidth - toolbarRect.width - 10
        }
        if (top + toolbarRect.height > window.innerHeight - 10) {
          top = imageRect.top - toolbarRect.height - 10
        }
        
        toolbar.style.left = `${left}px`
        toolbar.style.top = `${top}px`
      })
    },

    // 更新编辑器内容
    updateEditorContent() {
      if (this.$refs.editor && this.$refs.editor.quill) {
        // 获取当前内容并同步到 article.content
        const content = this.$refs.editor.quill.root.innerHTML
        this.article.content = content
        
        // 触发编辑器内容变化事件，但不重新设置内容
        this.$emit('change', content)
      }
      this.updateHistoryState()
    },

    updateHistoryState() {
      const editor = this.$refs.editor && this.$refs.editor.quill
      const history = editor && editor.history

      if (!history) {
        this.canUndo = false
        this.canRedo = false
        return
      }

      this.canUndo = Array.isArray(history.stack && history.stack.undo) && history.stack.undo.length > 0
      this.canRedo = Array.isArray(history.stack && history.stack.redo) && history.stack.redo.length > 0
    },

    undoEditor() {
      const editor = this.$refs.editor && this.$refs.editor.quill
      if (!editor || !editor.history) return

      editor.history.undo()
      this.updateEditorContent()
      this.updateToolbarState()
      this.$nextTick(() => {
        editor.focus()
      })
    },

    redoEditor() {
      const editor = this.$refs.editor && this.$refs.editor.quill
      if (!editor || !editor.history) return

      editor.history.redo()
      this.updateEditorContent()
      this.updateToolbarState()
      this.$nextTick(() => {
        editor.focus()
      })
    },

    getBodyImageCropFrameSize() {
      const frame = this.$refs.bodyImageCropFrame
      return {
        width: frame ? frame.clientWidth : 720,
        height: frame ? frame.clientHeight : 420
      }
    },

    async openBodyImageCropDialog() {
      if (!this.selectedImage) return

      try {
        this.bodyImageCropTarget = this.selectedImage
        this.bodyImageCropImageUrl = this.selectedImage.currentSrc || this.selectedImage.src
        this.bodyImageCropDialogVisible = true

        this.bodyImageCropNaturalWidth = this.selectedImage.naturalWidth
        this.bodyImageCropNaturalHeight = this.selectedImage.naturalHeight

        if (!this.bodyImageCropNaturalWidth || !this.bodyImageCropNaturalHeight) {
          throw new Error('图片尺寸读取失败')
        }

        this.$nextTick(() => {
          this.resetBodyImageCrop()
        })
      } catch (error) {
        console.error('打开正文图片裁剪失败:', error)
        this.$message.error('当前图片暂时无法裁剪，请重新上传后再试')
      }
    },

    clampBodyImageCropPosition() {
      const { width: frameWidth, height: frameHeight } = this.getBodyImageCropFrameSize()
      const scaledWidth = this.bodyImageCropNaturalWidth * this.bodyImageCropScale
      const scaledHeight = this.bodyImageCropNaturalHeight * this.bodyImageCropScale

      const minX = Math.min(0, frameWidth - scaledWidth)
      const minY = Math.min(0, frameHeight - scaledHeight)
      const maxX = scaledWidth <= frameWidth ? (frameWidth - scaledWidth) / 2 : 0
      const maxY = scaledHeight <= frameHeight ? (frameHeight - scaledHeight) / 2 : 0

      this.bodyImageCropX = Math.min(maxX, Math.max(minX, this.bodyImageCropX))
      this.bodyImageCropY = Math.min(maxY, Math.max(minY, this.bodyImageCropY))
    },

    resetBodyImageCrop() {
      const { width: frameWidth, height: frameHeight } = this.getBodyImageCropFrameSize()
      const scaleX = frameWidth / this.bodyImageCropNaturalWidth
      const scaleY = frameHeight / this.bodyImageCropNaturalHeight
      const baseScale = Math.max(scaleX, scaleY)

      this.bodyImageCropMinScale = baseScale
      this.bodyImageCropMaxScale = Math.max(baseScale * 3, baseScale + 1)
      this.bodyImageCropScale = baseScale
      this.bodyImageCropLastScale = baseScale
      this.centerBodyImageCrop()
    },

    centerBodyImageCrop() {
      const { width: frameWidth, height: frameHeight } = this.getBodyImageCropFrameSize()
      const scaledWidth = this.bodyImageCropNaturalWidth * this.bodyImageCropScale
      const scaledHeight = this.bodyImageCropNaturalHeight * this.bodyImageCropScale

      this.bodyImageCropX = (frameWidth - scaledWidth) / 2
      this.bodyImageCropY = (frameHeight - scaledHeight) / 2
      this.clampBodyImageCropPosition()
    },

    handleBodyImageScaleInput(value) {
      const { width: frameWidth, height: frameHeight } = this.getBodyImageCropFrameSize()
      const previousScale = this.bodyImageCropLastScale || value
      const focusX = (frameWidth / 2 - this.bodyImageCropX) / previousScale
      const focusY = (frameHeight / 2 - this.bodyImageCropY) / previousScale

      this.bodyImageCropScale = value
      this.bodyImageCropX = frameWidth / 2 - focusX * value
      this.bodyImageCropY = frameHeight / 2 - focusY * value
      this.bodyImageCropLastScale = value
      this.clampBodyImageCropPosition()
    },

    startBodyImageDrag(event) {
      if (!this.bodyImageCropImageUrl) return
      this.bodyImageCropDragging = true
      this.bodyImageCropDragStartX = event.clientX
      this.bodyImageCropDragStartY = event.clientY
      this.bodyImageCropOriginX = this.bodyImageCropX
      this.bodyImageCropOriginY = this.bodyImageCropY
      document.addEventListener('mousemove', this.handleBodyImageDrag)
      document.addEventListener('mouseup', this.endBodyImageDrag)
    },

    handleBodyImageDrag(event) {
      if (!this.bodyImageCropDragging) return
      this.bodyImageCropX = this.bodyImageCropOriginX + event.clientX - this.bodyImageCropDragStartX
      this.bodyImageCropY = this.bodyImageCropOriginY + event.clientY - this.bodyImageCropDragStartY
      this.clampBodyImageCropPosition()
    },

    endBodyImageDrag() {
      this.bodyImageCropDragging = false
      document.removeEventListener('mousemove', this.handleBodyImageDrag)
      document.removeEventListener('mouseup', this.endBodyImageDrag)
    },

    closeBodyImageCropDialog() {
      this.endBodyImageDrag()
      this.bodyImageCropDialogVisible = false
      this.bodyImageCropImageUrl = ''
      this.bodyImageCropTarget = null
    },

    async applyBodyImageCrop() {
      if (!this.bodyImageCropTarget || !this.bodyImageCropImageUrl) return

      try {
        this.uploadingBodyImageCrop = true

        const sourceImage = await new Promise((resolve, reject) => {
          const image = new Image()
          image.onload = () => resolve(image)
          image.onerror = reject
          image.src = this.bodyImageCropImageUrl
        })

        const { width: frameWidth, height: frameHeight } = this.getBodyImageCropFrameSize()
        const outputWidth = 1200
        const outputHeight = Math.round(outputWidth * (frameHeight / frameWidth))
        const canvas = document.createElement('canvas')
        canvas.width = outputWidth
        canvas.height = outputHeight
        const ctx = canvas.getContext('2d')

        const sx = -this.bodyImageCropX / this.bodyImageCropScale
        const sy = -this.bodyImageCropY / this.bodyImageCropScale
        const sWidth = frameWidth / this.bodyImageCropScale
        const sHeight = frameHeight / this.bodyImageCropScale

        ctx.drawImage(
          sourceImage,
          sx,
          sy,
          sWidth,
          sHeight,
          0,
          0,
          outputWidth,
          outputHeight
        )

        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, 'image/jpeg', 0.92)
        })

        if (!blob) {
          throw new Error('裁剪图片生成失败')
        }

        const croppedFile = new File([blob], `article-crop-${Date.now()}.jpg`, {
          type: 'image/jpeg'
        })
        const newImageUrl = await this.uploadPastedImage(croppedFile)

        if (newImageUrl) {
          this.bodyImageCropTarget.src = newImageUrl
          this.selectedImage = this.bodyImageCropTarget
          this.updateEditorContent()
          this.closeBodyImageCropDialog()
          this.positionImageToolbar(this.selectedImage)
          this.$message.success('正文图片已替换为裁剪版本')
        }
      } catch (error) {
        console.error('正文图片裁剪失败:', error)
        this.$message.error('正文图片裁剪失败，请重试')
      } finally {
        this.uploadingBodyImageCrop = false
      }
    },

    // 初始化图片点击事件
    initImageClickEvents() {
      this.$nextTick(() => {
        const editor = this.$refs.editor
        if (!editor) return
        
        // 监听图片点击事件
        editor.$el.addEventListener('click', (event) => {
          if (event.target.tagName === 'IMG') {
            event.preventDefault()
            event.stopPropagation()
            this.openImageToolbar(event.target)
          }
        })
        
        // 点击其他地方隐藏工具栏
        document.addEventListener('click', (event) => {
          if (
            !event.target.closest('.image-toolbar') &&
            !event.target.closest('.ql-editor img') &&
            !event.target.closest('.el-dialog__wrapper')
          ) {
            this.hideImageToolbar()
          }
        })
      })
    },

    onEditorChange() {
      // 编辑器内容变化
      this.updateToolbarState()
      
      // 同步内容到 article.content
      if (this.$refs.editor && this.$refs.editor.quill) {
        const content = this.$refs.editor.quill.root.innerHTML
        this.article.content = content
      }
      this.updateHistoryState()
    },

    // 更新工具栏状态
    updateToolbarState() {
      const editor = this.$refs.editor.quill
      if (!editor) return

      const format = editor.getFormat()
      
      // 更新按钮激活状态
      this.$nextTick(() => {
        const buttons = document.querySelectorAll('.toolbar-btn')
        buttons.forEach(btn => {
          const action = btn.getAttribute('data-action')
          const value = btn.getAttribute('data-value')
          
          if (action === 'bold' && format.bold) {
            btn.classList.add('active')
          } else if (action === 'italic' && format.italic) {
            btn.classList.add('active')
          } else if (action === 'underline' && format.underline) {
            btn.classList.add('active')
          } else if (action === 'strike' && format.strike) {
            btn.classList.add('active')
          } else if (action === 'align' && format.align === value) {
            btn.classList.add('active')
          } else if (action === 'list' && format.list === value) {
            btn.classList.add('active')
          } else if (action === 'blockquote' && format.blockquote) {
            btn.classList.add('active')
          } else if (action === 'code-block' && format['code-block']) {
            btn.classList.add('active')
          } else {
            btn.classList.remove('active')
          }
        })
      })
    },

    // 处理粘贴事件，支持图片粘贴
    handlePaste(event) {
      console.log('🔍 粘贴事件触发')
      console.log('📋 剪贴板类型:', event.clipboardData.types)
      
      // 检查是否包含图片数据
      const hasImage = event.clipboardData.types.some(type => type.startsWith('image/'))
      console.log('🖼️ 是否包含图片:', hasImage)
      
      if (hasImage) {
        event.preventDefault()
        event.stopPropagation()
        this.handleImagePaste(event)
      }
    },

    // 处理图片粘贴
    async handleImagePaste(event) {
      try {
        console.log('🖼️ 开始处理图片粘贴')
        const items = event.clipboardData.items
        console.log('📦 剪贴板项目数量:', items.length)
        let imageCount = 0
        
        for (let i = 0; i < items.length; i++) {
          const item = items[i]
          console.log(`📦 项目 ${i}:`, item.type)
          
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile()
            console.log('📁 获取到文件:', file ? {
              name: file.name,
              size: file.size,
              type: file.type
            } : 'null')
            
            if (file) {
              imageCount++
              // 显示上传进度
              this.$message.info(`正在上传第 ${imageCount} 张图片...`)
              
              // 验证文件大小
              if (file.size > 50 * 1024 * 1024) {
                this.$message.error(`图片 ${imageCount} 大小超过50MB，已跳过`)
                continue
              }
              
              // 上传图片
              console.log('🚀 开始上传图片...')
              const imageUrl = await this.uploadPastedImage(file)
              console.log('✅ 上传完成，图片URL:', imageUrl)
              
              if (imageUrl) {
                // 在编辑器中插入图片
                const editor = this.$refs.editor.quill
                const range = editor.getSelection()
                if (range) {
                  editor.insertEmbed(range.index, 'image', imageUrl)
                  editor.setSelection(range.index + 1)
                  
                  // 为新插入的图片添加默认尺寸类
                  this.$nextTick(() => {
                    const images = editor.root.querySelectorAll('img')
                    const lastImage = images[images.length - 1]
                    if (lastImage) {
                      lastImage.classList.add('image-adaptive')
                    }
                  })
                }
                this.$message.success(`第 ${imageCount} 张图片插入成功`)
              }
            }
          }
        }
      } catch (error) {
        console.error('❌ 处理图片粘贴失败:', error)
        this.$message.error('图片粘贴处理失败')
      }
    },

    // 上传粘贴的图片
    async uploadPastedImage(file) {
      try {
        const formData = new FormData()
        formData.append('image', file)
        
        const response = await apiClient.post('/upload/article-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        if (response && response.status === 'success') {
          return response.data && response.data.url
        } else {
          throw new Error((response && response.message) || '上传失败')
        }
      } catch (error) {
        console.error('❌ 上传图片失败:', error)
        this.$message.error('图片上传失败')
        return null
      }
    },

    // 处理混合内容粘贴（文本+图片）
    async handleMixedContentPaste(event) {
      try {
        console.log('🔄 处理混合内容粘贴')
        const items = event.clipboardData.items
        let textContent = ''
        let imageFiles = []
        
        // 分离文本和图片
        for (let i = 0; i < items.length; i++) {
          const item = items[i]
          if (item.type === 'text/plain') {
            const text = item.getAsString()
            textContent += text
          } else if (item.type.startsWith('image/')) {
            const file = item.getAsFile()
            if (file) {
              imageFiles.push(file)
            }
          }
        }
        
        // 先插入文本
        if (textContent.trim()) {
          const editor = this.$refs.editor.quill
          const range = editor.getSelection()
          if (range) {
            editor.insertText(range.index, textContent)
          }
        }
        
        // 再上传并插入图片
        for (const file of imageFiles) {
          const imageUrl = await this.uploadPastedImage(file)
          if (imageUrl) {
            const editor = this.$refs.editor.quill
            const range = editor.getSelection()
            if (range) {
              editor.insertEmbed(range.index, 'image', imageUrl)
              editor.setSelection(range.index + 1)
              
              // 为新插入的图片添加默认尺寸类
              this.$nextTick(() => {
                const images = editor.root.querySelectorAll('img')
                const lastImage = images[images.length - 1]
                if (lastImage) {
                  lastImage.classList.add('image-adaptive')
                }
              })
            }
          }
        }
        
        this.$message.success('混合内容粘贴完成')
      } catch (error) {
        console.error('❌ 处理混合内容粘贴失败:', error)
        this.$message.error('混合内容粘贴失败')
      }
    },

    // 处理 Word 文本（只处理文本，忽略图片）
    handleWordTextOnly(event) {
      try {
        console.log('📄 处理 Word 文本')
        const text = event.clipboardData.getData('text/plain')
        if (text.trim()) {
          const editor = this.$refs.editor.quill
          const range = editor.getSelection()
          if (range) {
            editor.insertText(range.index, text)
          }
          this.$message.info('已插入文本内容，图片请单独上传')
        }
      } catch (error) {
        console.error('❌ 处理 Word 文本失败:', error)
        this.$message.error('文本处理失败')
      }
    },

    // 增强的粘贴处理
    handleEnhancedPaste(event, editor) {
      console.log('🔍 增强粘贴处理')
      console.log('📋 剪贴板类型:', Array.from(event.clipboardData.types))
      
      // 检查是否包含图片数据
      const hasImage = event.clipboardData.types.some(type => type.startsWith('image/'))
      const hasFiles = event.clipboardData.types.includes('Files')
      const hasRtf = event.clipboardData.types.includes('text/rtf')
      const hasHtml = event.clipboardData.types.includes('text/html')
      const hasText = event.clipboardData.types.includes('text/plain')
      
      // 如果是 Word 格式（包含 RTF），只处理文本，提示用户单独插入图片
      if (hasRtf && (hasImage || hasFiles)) {
        console.log('📄 检测到 Word 格式的混合内容，只处理文本')
        event.preventDefault()
        this.handleWordTextOnly(event)
      } else if (hasFiles || hasImage) {
        console.log('🔄 检测到包含图片的内容，使用自定义处理')
        event.preventDefault()
        this.handleMixedContentPaste(event)
      }
    },

    // 处理工具栏图片上传
    handleImageUpload() {
      // 创建隐藏的文件输入
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.style.display = 'none'
      
      input.onchange = async (event) => {
        const file = event.target.files[0]
        if (file) {
          // 验证文件大小
          if (file.size > 50 * 1024 * 1024) {
            this.$message.error('图片大小不能超过50MB')
            document.body.removeChild(input)
            return
          }
          
          try {
            this.$message.info('正在上传图片...')
            const imageUrl = await this.uploadPastedImage(file)
            if (imageUrl) {
              const editor = this.$refs.editor.quill
              const range = editor.getSelection()
              if (range) {
                editor.insertEmbed(range.index, 'image', imageUrl)
                editor.setSelection(range.index + 1)
              }
              this.$message.success('图片上传成功')
            }
          } catch (error) {
            console.error('上传图片失败:', error)
            this.$message.error('图片上传失败，请重试')
          }
        }
        
        // 清理
        document.body.removeChild(input)
      }
      
      document.body.appendChild(input)
      input.click()
    },

    // 上传 base64 图片
    async uploadBase64Image(base64Data) {
      try {
        console.log('🖼️ 开始上传 base64 图片')
        
        // 将 base64 转换为文件
        const response = await fetch(base64Data)
        const blob = await response.blob()
        const file = new File([blob], `pasted-image-${Date.now()}`, { type: blob.type })
        
        console.log('📁 转换后的文件:', {
          name: file.name,
          size: file.size,
          type: file.type
        })
        
        // 验证文件大小
        if (file.size > 50 * 1024 * 1024) {
          this.$message.error('图片大小不能超过50MB')
          return
        }
        
        this.$message.info('正在上传粘贴的图片...')
        const imageUrl = await this.uploadPastedImage(file)
        
        if (imageUrl) {
          const editor = this.$refs.editor.quill
          const range = editor.getSelection()
          if (range) {
            editor.insertEmbed(range.index, 'image', imageUrl)
            editor.setSelection(range.index + 1)
          }
          this.$message.success('图片上传成功')
        }
      } catch (error) {
        console.error('❌ 上传 base64 图片失败:', error)
        this.$message.error('图片上传失败，请重试')
      }
    },

    // 上传多张 base64 图片
    async uploadMultipleBase64Images(base64Images) {
      try {
        console.log(`🖼️ 开始上传 ${base64Images.length} 张 base64 图片`)
        
        this.$message.info(`正在上传 ${base64Images.length} 张图片...`)
        
        const editor = this.$refs.editor.quill
        const range = editor.getSelection()
        let insertIndex = range ? range.index : 0
        
        for (let i = 0; i < base64Images.length; i++) {
          const base64Data = base64Images[i]
          console.log(`📤 上传第 ${i + 1} 张图片`)
          
          try {
            // 将 base64 转换为文件
            const response = await fetch(base64Data)
            const blob = await response.blob()
            const file = new File([blob], `pasted-image-${Date.now()}-${i}`, { type: blob.type })
            
            // 验证文件大小
            if (file.size > 50 * 1024 * 1024) {
              console.warn(`图片 ${i + 1} 大小超过50MB，已跳过`)
              continue
            }
            
            // 上传图片
            const imageUrl = await this.uploadPastedImage(file)
            
            if (imageUrl) {
              // 插入图片
              editor.insertEmbed(insertIndex, 'image', imageUrl)
              insertIndex += 1
              
              // 如果不是最后一张图片，添加换行
              if (i < base64Images.length - 1) {
                editor.insertText(insertIndex, '\n')
                insertIndex += 1
              }
              
              console.log(`✅ 第 ${i + 1} 张图片上传成功`)
            }
          } catch (error) {
            console.error(`❌ 第 ${i + 1} 张图片上传失败:`, error)
          }
        }
        
        // 设置光标位置
        editor.setSelection(insertIndex)
        this.$message.success(`成功上传 ${base64Images.length} 张图片`)
        
      } catch (error) {
        console.error('❌ 批量上传 base64 图片失败:', error)
        this.$message.error('图片上传失败，请重试')
      }
    },

    async saveDraft() {
      console.log('🚀 saveDraft方法被调用')
      
      if (!this.canPersistArticle) {
        this.$message.warning('请输入标题和内容')
        return
      }

      // 如果文章当前是已发布状态，提醒用户
      if (this.article.status === 'published') {
        try {
          await this.$confirm(
            '当前文章是已发布状态，保存草稿会将文章状态改为草稿，文章将不再在列表中显示。确定要保存为草稿吗？',
            '确认保存草稿',
            {
              confirmButtonText: '保存草稿',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )
        } catch {
          // 用户取消
          return
        }
      }

      // 检查登录状态
      const token = localStorage.getItem('token')
      const user = localStorage.getItem('user')
      
      if (!token || !user) {
        this.$message.warning('请先登录后再保存草稿')
        return
      }
      
      // 验证Vuex状态与localStorage的一致性
      if (!this.$store.state.isAuthenticated || !this.$store.state.user) {
        console.log('🔄 Vuex状态不一致，尝试恢复认证状态')
        try {
          const restored = await this.$store.dispatch('checkAuth')
          if (!restored) {
            this.$message.warning('登录状态异常，请重新登录')
            return
          }
        } catch (error) {
          console.error('恢复认证状态失败:', error)
          this.$message.warning('登录状态异常，请重新登录')
          return
        }
      }

      try {
        this.saving = true
        this.saveState = 'saving'
        const articleData = {
          ...this.article,
          status: 'draft'
        }

        console.log('📝 开始保存草稿:', articleData)
        console.log('📊 数据大小:', JSON.stringify(articleData).length, '字节')
        
        let response
        if (this.isEdit) {
          console.log('📝 更新现有文章:', this.articleId)
          response = await apiClient.put(`/articles/${this.articleId}`, articleData)
        } else {
          console.log('📝 创建新文章')
          response = await apiClient.post('/articles', articleData)
          if (response.status === 'success') {
            this.isEdit = true
            this.articleId = response.data.id
            // 更新路由但不触发导航
            history.replaceState(null, '', `/articles/edit/${this.articleId}`)
          }
        }

        console.log('✅ 保存成功:', response)
        if (response.status === 'success') {
          if (response.data) {
            this.articleId = (response.data.id || this.articleId)
          }
          this.lastSavedAt = new Date()
          this.saveState = 'saved'
          this.$message.success('草稿已保存')
        }
      } catch (error) {
        console.error('❌ 保存草稿失败:', error)
        console.error('❌ 错误详情:', {
          status: error.response && error.response.status,
          statusText: error.response && error.response.statusText,
          data: error.response && error.response.data,
          message: error.message
        })
        
        // 根据错误类型显示不同的消息
        if (error.response && error.response.status === 413) {
          this.$message.error('文章内容过大，请删减部分内容后重试')
        } else if (error.response && error.response.status === 401) {
          const errorMessage = (error.response.data && error.response.data.message) || ''
          if (errorMessage.includes('The user belonging to this token no longer exists')) {
            this.$message.error('用户账户不存在，请重新登录')
            // 清除用户数据并跳转到登录页面
            this.$store.dispatch('logout')
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            this.$router.push('/login')
          } else {
            this.$message.error('登录已过期，请重新登录')
            // 尝试重新验证token
            try {
              const restored = await this.$store.dispatch('checkAuth')
              if (!restored) {
                this.$store.dispatch('logout')
                localStorage.removeItem('token')
                localStorage.removeItem('user')
                this.$router.push('/login')
              }
            } catch (authError) {
              console.error('重新验证失败:', authError)
              this.$store.dispatch('logout')
              localStorage.removeItem('token')
              localStorage.removeItem('user')
              this.$router.push('/login')
            }
          }
        } else {
          const errorMsg = (error.response && error.response.data && error.response.data.message) || error.message || '网络错误'
          this.saveState = 'error'
          this.$message.error('保存草稿失败：' + errorMsg)
        }
      } finally {
        this.saving = false
      }
    },

    async autoSave() {
      if (!this.canPersistArticle) return
      
      try {
        this.saveState = 'saving'
        // 自动保存时保持原有状态，不改变文章状态
        const articleData = {
          ...this.article
        }

        if (this.isEdit) {
          await apiClient.put(`/articles/${this.articleId}`, articleData)
          console.log('✅ 自动保存成功，保持状态:', this.article.status)
        } else {
          const response = await apiClient.post('/articles', articleData)
          if (response.status === 'success') {
            this.isEdit = true
            this.articleId = response.data.id
            history.replaceState(null, '', `/articles/edit/${this.articleId}`)
          }
        }
        this.lastSavedAt = new Date()
        this.saveState = 'saved'
      } catch (error) {
        console.error('自动保存失败:', error)
        this.saveState = 'error'
        // 如果是401错误，记录详细信息但不显示给用户（避免频繁弹窗）
        if (error.response && error.response.status === 401) {
          console.log('自动保存遇到401错误，可能是认证问题')
          // 不在这里清除用户数据，让用户手动操作时再处理
        }
      }
    },

    showPublishDialog() {
      if (!this.canPersistArticle) {
        this.$message.warning('请输入标题和内容')
        return
      }

      // 同步数据到发布表单
      this.publishForm.category = this.article.category
      this.publishForm.tags = [...this.article.tags]
      this.publishForm.featured = this.article.featured

      this.publishDialogVisible = true
    },

    async publishArticle() {
      try {
        this.publishing = true
        
        // 显示发布中的提示
        this.$message.info('正在发布文章，请稍候...')
        
        // 检查内容是否完整
        if (!this.canPersistArticle) {
          this.$message.warning('请输入标题和内容')
          return
        }

        // 检查登录状态
        const token = localStorage.getItem('token')
        const user = localStorage.getItem('user')
        
        console.log('🔐 认证状态检查:', {
          hasToken: !!token,
          hasUser: !!user,
          token: token ? token.substring(0, 20) + '...' : null,
          user: user ? JSON.parse(user).username : null
        })
        
        if (!token || !user) {
          this.$message.warning('请先登录后再发布文章')
          return
        }

        // 直接发布文章，不先保存草稿
        const articleData = {
          ...this.article,
          ...this.publishForm,
          status: 'published',
          publishedAt: new Date()
        }

        console.log('📝 开始发布文章:', articleData)
        console.log('📊 文章数据详情:', {
          title: articleData.title,
          contentLength: articleData.content ? articleData.content.length : 0,
          summary: articleData.summary,
          category: articleData.category,
          tags: articleData.tags,
          status: articleData.status,
          hasContent: this.hasContent
        })
        
        let response
        if (this.isEdit) {
          console.log('📝 更新现有文章:', this.articleId)
          response = await apiClient.put(`/articles/${this.articleId}`, articleData)
        } else {
          console.log('📝 创建新文章')
          response = await apiClient.post('/articles', articleData)
          if (response.status === 'success') {
            this.isEdit = true
            this.articleId = response.data.id
            // 更新路由但不触发导航
            history.replaceState(null, '', `/articles/edit/${this.articleId}`)
          }
        }
        
        if (response.status === 'success') {
          this.lastSavedAt = new Date()
          this.saveState = 'saved'
          // 显示成功提示并延迟跳转
          this.$message({
            message: '🎉 文章发布成功！正在跳转到文章页面...',
            type: 'success',
            duration: 3000,
            showClose: true
          })
          
          this.publishDialogVisible = false
          
          // 延迟跳转，让用户看到成功提示
          setTimeout(() => {
            this.$router.push(`/articles/${this.articleId}`)
          }, 1500)
        } else {
          // API返回失败状态
          this.$message.error(response.message || '发布失败，请重试')
        }
      } catch (error) {
        console.error('发布文章失败:', error)
        console.error('❌ 错误详情:', {
          status: error.response && error.response.status,
          statusText: error.response && error.response.statusText,
          data: error.response && error.response.data,
          message: error.message,
          config: error.config ? {
            url: error.config.url,
            method: error.config.method,
            headers: error.config.headers
          } : null
        })
        
        // 根据错误类型显示不同的消息
        if (error.response && error.response.status === 413) {
          this.$message.error('文章内容过大，请删减部分内容后重试')
        } else if (error.response && error.response.status === 401) {
          this.$message.error('登录状态异常，请重新登录')
        } else if (error.response && error.response.status === 403) {
          this.$message.error('权限不足，无法发布文章')
        } else if (error.response && error.response.status === 422) {
          this.$message.error('文章格式有误，请检查内容')
        } else if (error.response && error.response.data && error.response.data.message) {
          this.$message.error(error.response.data.message)
        } else {
          this.$message.error('发布文章失败，请检查网络连接后重试')
        }
        this.saveState = 'error'
      } finally {
        this.publishing = false
      }
    },

    showTagInput() {
      this.tagInputVisible = true
      this.$nextTick(() => {
        this.$refs.tagInput.$refs.input.focus()
      })
    },

    addTag() {
      const value = this.tagInputValue.trim()
      if (value && !this.publishForm.tags.includes(value)) {
        this.publishForm.tags.push(value)
      }
      this.tagInputVisible = false
      this.tagInputValue = ''
    },

    showArticleTagInput() {
      this.articleTagInputVisible = true
      this.$nextTick(() => {
        if (this.$refs.articleTagInput && this.$refs.articleTagInput.$refs.input) {
          this.$refs.articleTagInput.$refs.input.focus()
        }
      })
    },

    addArticleTag() {
      const value = this.articleTagInputValue.trim()
      if (value && !this.article.tags.includes(value)) {
        this.article.tags.push(value)
      }
      this.articleTagInputVisible = false
      this.articleTagInputValue = ''
    },

    removeArticleTag(tag) {
      const index = this.article.tags.indexOf(tag)
      if (index > -1) {
        this.article.tags.splice(index, 1)
      }
    },

    removeTag(tag) {
      const index = this.publishForm.tags.indexOf(tag)
      if (index > -1) {
        this.publishForm.tags.splice(index, 1)
      }
    },

    changeCover() {
      this.triggerCoverFileInput()
    },

    removeCover() {
      this.article.coverImage = ''
    },

    validateImageFile(file) {
      const isImage = file.type.startsWith('image/')
      const isLt5M = file.size / 1024 / 1024 < 50

      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 50MB!')
        return false
      }
      return true
    },

    beforeCoverUpload(file) {
      if (!this.validateImageFile(file)) {
        return false
      }
      this.openCoverCropDialog(file)
      return false
    },

    triggerCoverFileInput() {
      if (this.$refs.coverFileInput) {
        this.$refs.coverFileInput.click()
      }
    },

    handleCoverFileChange(event) {
      const [file] = event.target.files || []
      if (file && this.validateImageFile(file)) {
        this.openCoverCropDialog(file)
      }
      event.target.value = ''
    },

    getCoverCropFrameSize() {
      const frame = this.$refs.coverCropFrame
      return {
        width: frame ? frame.clientWidth : 720,
        height: frame ? frame.clientHeight : 405
      }
    },

    async openCoverCropDialog(file) {
      if (this.coverCropImageUrl) {
        URL.revokeObjectURL(this.coverCropImageUrl)
      }

      const objectUrl = URL.createObjectURL(file)
      this.coverCropFile = file
      this.coverCropImageUrl = objectUrl
      this.coverCropDialogVisible = true

      const imageMeta = await new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve({
          width: image.naturalWidth,
          height: image.naturalHeight
        })
        image.onerror = reject
        image.src = objectUrl
      })

      this.coverCropNaturalWidth = imageMeta.width
      this.coverCropNaturalHeight = imageMeta.height

      this.$nextTick(() => {
        this.resetCoverCrop()
      })
    },

    clampCoverCropPosition() {
      const { width: frameWidth, height: frameHeight } = this.getCoverCropFrameSize()
      const scaledWidth = this.coverCropNaturalWidth * this.coverCropScale
      const scaledHeight = this.coverCropNaturalHeight * this.coverCropScale

      const minX = Math.min(0, frameWidth - scaledWidth)
      const minY = Math.min(0, frameHeight - scaledHeight)
      const maxX = scaledWidth <= frameWidth ? (frameWidth - scaledWidth) / 2 : 0
      const maxY = scaledHeight <= frameHeight ? (frameHeight - scaledHeight) / 2 : 0

      this.coverCropX = Math.min(maxX, Math.max(minX, this.coverCropX))
      this.coverCropY = Math.min(maxY, Math.max(minY, this.coverCropY))
    },

    resetCoverCrop() {
      const { width: frameWidth, height: frameHeight } = this.getCoverCropFrameSize()
      const scaleX = frameWidth / this.coverCropNaturalWidth
      const scaleY = frameHeight / this.coverCropNaturalHeight
      const baseScale = Math.max(scaleX, scaleY)

      this.coverCropMinScale = baseScale
      this.coverCropMaxScale = Math.max(baseScale * 3, baseScale + 1)
      this.coverCropScale = baseScale
      this.coverCropLastScale = baseScale
      this.centerCoverCrop()
    },

    centerCoverCrop() {
      const { width: frameWidth, height: frameHeight } = this.getCoverCropFrameSize()
      const scaledWidth = this.coverCropNaturalWidth * this.coverCropScale
      const scaledHeight = this.coverCropNaturalHeight * this.coverCropScale

      this.coverCropX = (frameWidth - scaledWidth) / 2
      this.coverCropY = (frameHeight - scaledHeight) / 2
      this.clampCoverCropPosition()
    },

    handleCoverScaleInput(value) {
      const { width: frameWidth, height: frameHeight } = this.getCoverCropFrameSize()
      const previousScale = this.coverCropLastScale || value
      const focusX = (frameWidth / 2 - this.coverCropX) / previousScale
      const focusY = (frameHeight / 2 - this.coverCropY) / previousScale

      this.coverCropScale = value
      this.coverCropX = frameWidth / 2 - focusX * value
      this.coverCropY = frameHeight / 2 - focusY * value
      this.coverCropLastScale = value
      this.clampCoverCropPosition()
    },

    startCoverDrag(event) {
      if (!this.coverCropImageUrl) return
      this.coverCropDragging = true
      this.coverCropDragStartX = event.clientX
      this.coverCropDragStartY = event.clientY
      this.coverCropOriginX = this.coverCropX
      this.coverCropOriginY = this.coverCropY
      document.addEventListener('mousemove', this.handleCoverDrag)
      document.addEventListener('mouseup', this.endCoverDrag)
    },

    handleCoverDrag(event) {
      if (!this.coverCropDragging) return
      this.coverCropX = this.coverCropOriginX + event.clientX - this.coverCropDragStartX
      this.coverCropY = this.coverCropOriginY + event.clientY - this.coverCropDragStartY
      this.clampCoverCropPosition()
    },

    endCoverDrag() {
      this.coverCropDragging = false
      document.removeEventListener('mousemove', this.handleCoverDrag)
      document.removeEventListener('mouseup', this.endCoverDrag)
    },

    closeCoverCropDialog() {
      this.endCoverDrag()
      if (this.coverCropImageUrl) {
        URL.revokeObjectURL(this.coverCropImageUrl)
      }
      this.coverCropDialogVisible = false
      this.coverCropImageUrl = ''
      this.coverCropFile = null
    },

    async uploadCroppedCover() {
      if (!this.coverCropImageUrl || !this.coverCropFile) return

      try {
        this.uploadingCover = true
        const sourceImage = await new Promise((resolve, reject) => {
          const image = new Image()
          image.onload = () => resolve(image)
          image.onerror = reject
          image.src = this.coverCropImageUrl
        })

        const { width: frameWidth, height: frameHeight } = this.getCoverCropFrameSize()
        const outputWidth = 1200
        const outputHeight = Math.round(outputWidth * (frameHeight / frameWidth))
        const canvas = document.createElement('canvas')
        canvas.width = outputWidth
        canvas.height = outputHeight
        const ctx = canvas.getContext('2d')

        const sx = -this.coverCropX / this.coverCropScale
        const sy = -this.coverCropY / this.coverCropScale
        const sWidth = frameWidth / this.coverCropScale
        const sHeight = frameHeight / this.coverCropScale

        ctx.drawImage(
          sourceImage,
          sx,
          sy,
          sWidth,
          sHeight,
          0,
          0,
          outputWidth,
          outputHeight
        )

        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, this.coverCropFile.type || 'image/jpeg', 0.92)
        })

        if (!blob) {
          throw new Error('裁剪结果生成失败')
        }

        const formData = new FormData()
        formData.append('file', blob, this.coverCropFile.name || `cover-${Date.now()}.jpg`)

        const response = await apiClient.post('/upload/cover', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        this.handleCoverSuccess(response)
        this.closeCoverCropDialog()
      } catch (error) {
        console.error('封面裁剪上传失败:', error)
        this.$message.error('封面裁剪上传失败，请重试')
      } finally {
        this.uploadingCover = false
      }
    },

    handleCoverSuccess(response) {
      console.log('上传响应:', response)
      if (response.success || response.status === 'success') {
        this.article.coverImage = (response.data && response.data.url) || response.url
        this.lastSavedAt = new Date()
        this.$message.success('封面上传成功')
      } else {
        this.$message.error('封面上传失败')
      }
    },

    handleUploadError() {
      this.$message.error('上传失败，请重试')
    },

    handleBack() {
      if (this.hasContent && this.isEdit) {
        this.$confirm('确定要离开吗？未保存的更改将会丢失，但不会影响原文章。', '提示', {
          confirmButtonText: '离开',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.$router.go(-1)
        })
      } else {
        this.$router.go(-1)
      }
    },

    // 创建新文章
    createNewArticle() {
      this.$confirm('确定要创建新文章吗？当前未保存的内容将会丢失。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.article = {
          title: '',
          subtitle: '',
          author: '',
          content: '',
          summary: '',
          coverImage: '',
          category: '其他',
          tags: [],
          featured: false,
          status: 'draft'
        }
        this.isEdit = false
        this.articleId = null
        this.lastSavedAt = null
        this.saveState = 'idle'
        this.$router.push('/articles/edit')
      })
    },

    // 显示历史版本
    showHistory() {
      this.$message.info('历史版本功能开发中...')
    },

    formatDateTime(dateValue) {
      const date = new Date(dateValue)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // 格式化文本
    formatText(format, value) {
      const editor = this.$refs.editor.quill
      if (!editor) return

      switch (format) {
        case 'bold':
          editor.format('bold', !editor.getFormat().bold)
          break
        case 'italic':
          editor.format('italic', !editor.getFormat().italic)
          break
        case 'underline':
          editor.format('underline', !editor.getFormat().underline)
          break
        case 'strike':
          editor.format('strike', !editor.getFormat().strike)
          break
        case 'color':
          const color = prompt('请输入颜色值（如：#ff0000）')
          if (color) editor.format('color', color)
          break
        case 'background':
          const bgColor = prompt('请输入背景色值（如：#ffff00）')
          if (bgColor) editor.format('background', bgColor)
          break
        case 'align':
          editor.format('align', value)
          break
        case 'list':
          editor.format('list', value)
          break
        case 'indent':
          const currentIndent = editor.getFormat().indent || 0
          editor.format('indent', Math.max(0, currentIndent + value))
          break
        case 'blockquote':
          editor.format('blockquote', !editor.getFormat().blockquote)
          break
        case 'code-block':
          editor.format('code-block', !editor.getFormat()['code-block'])
          break
        case 'clean':
          editor.removeFormat()
          break
      }
      
      // 更新工具栏状态
      this.$nextTick(() => {
        this.updateToolbarState()
      })
    },

    // 插入链接
    insertLink() {
      const url = prompt('请输入链接地址：')
      if (url) {
        const editor = this.$refs.editor.quill
        const range = editor.getSelection()
        if (range) {
          editor.insertEmbed(range.index, 'link', url)
        }
      }
    },

    // 插入视频
    insertVideo() {
      const url = prompt('请输入视频地址：')
      if (url) {
        const editor = this.$refs.editor.quill
        const range = editor.getSelection()
        if (range) {
          editor.insertEmbed(range.index, 'video', url)
        }
      }
    },

    // 处理搜索
    handleSearch() {
      if (!this.searchKeyword.trim()) return;
      this.$router.push({
        path: '/search',
        query: { keyword: this.searchKeyword.trim() }
      });
    }
  }
}
</script>

<style scoped>
.article-editor-page {
  min-height: 100vh;
  background: #f5f6f7;
}

.editor-topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  height: 72px;
  background: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid #ebeef5;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 0 24px;
}

.topbar-left,
.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.topbar-center {
  flex: 1;
  display: flex;
  justify-content: center;
}

.back-button {
  color: #606266;
}

.editor-brand {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.brand-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2329;
}

.brand-subtitle {
  font-size: 12px;
  color: #909399;
}

.save-time {
  margin-left: 8px;
}

.editor-meta-pill {
  display: inline-flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  border-radius: 999px;
  background: #f7f8fa;
  color: #606266;
  font-size: 13px;
}

.editor-shell {
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
}

.editor-workspace {
  min-width: 0;
  position: relative;
}

.workspace-header {
  position: relative;
  z-index: 15;
  margin-bottom: 16px;
  padding-bottom: 8px;
  background: linear-gradient(180deg, #f5f6f7 0%, rgba(245, 246, 247, 0.96) 80%, rgba(245, 246, 247, 0) 100%);
}

.workspace-header.is-fixed {
  position: fixed;
  z-index: 25;
}

.workspace-header-placeholder {
  width: 100%;
}

.sticky-toolbar-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px;
  border: 1px solid rgba(235, 238, 245, 0.95);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 12px 28px rgba(31, 35, 41, 0.08);
  backdrop-filter: blur(12px);
}

.mode-switch {
  display: inline-flex;
  padding: 4px;
  background: #eef1f6;
  border-radius: 12px;
}

.mode-switch-btn {
  border: none;
  background: transparent;
  color: #606266;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mode-switch-btn.active {
  background: #ffffff;
  color: #e03426;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.toolbar-wrap {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.edit-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: max-content;
  padding: 2px 0 6px;
}

.toolbar-follow-tip {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: #909399;
}

.toolbar-divider {
  width: 1px;
  height: 24px;
  background: #ebeef5;
  flex-shrink: 0;
}

.workspace-body {
  min-width: 0;
}

.floating-image-btn {
  position: fixed;
  right: 392px;
  bottom: 28px;
  z-index: 16;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 999px;
  padding: 12px 18px;
  background: #e03426;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 12px 24px rgba(224, 52, 38, 0.24);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.floating-image-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 30px rgba(224, 52, 38, 0.32);
}

.editor-card,
.preview-card,
.sidebar-card {
  background: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(31, 35, 41, 0.05);
}

.editor-card {
  padding: 32px;
}

.editor-card-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.header-label {
  font-size: 16px;
  font-weight: 600;
  color: #1f2329;
}

.header-hint {
  font-size: 13px;
  color: #909399;
}

.input-block {
  margin-bottom: 24px;
}

.section-label,
.field-label {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
}

.title-input >>> .el-input__inner {
  border: none;
  font-size: 38px;
  font-weight: 700;
  line-height: 1.2;
  color: #1f2329;
  padding: 12px 0;
  background: transparent;
  border-bottom: 1px solid #ebeef5;
  height: auto;
}

.title-input >>> .el-input__inner:focus {
  border-bottom-color: #e03426;
  box-shadow: none;
}

.title-input >>> .el-input__inner::placeholder {
  color: #c0c4cc;
  font-weight: 400;
}

.subtitle-input >>> .el-input__inner {
  border: none;
  font-size: 18px;
  line-height: 1.6;
  color: #606266;
  padding: 0 0 14px;
  background: transparent;
  border-bottom: 1px solid #ebeef5;
  height: auto;
}

.subtitle-input >>> .el-input__inner:focus {
  border-bottom-color: #e03426;
  box-shadow: none;
}

.cover-upload {
  border: 2px dashed #d3dce6;
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  background: #fafbfd;
}

.cover-upload:hover {
  border-color: #e03426;
  background: #fffaf9;
}

.cover-upload >>> .el-upload-dragger {
  border: none;
  background: transparent;
  width: 100%;
  height: auto;
}

.upload-text p {
  margin: 6px 0;
  color: #606266;
}

.upload-hint {
  font-size: 12px;
  color: #909399;
}

.cover-preview {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
}

.sidebar-cover-preview img {
  width: 100%;
  height: 180px;
  object-fit: cover;
}

.cover-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.cover-preview:hover .cover-overlay {
  opacity: 1;
}

.cover-overlay .el-button {
  color: white;
  border-color: white;
}

.cover-overlay .el-button:hover {
  background: white;
  color: #e03426;
}

/* 摘要输入 */
.summary-input >>> .el-textarea__inner {
  border: 1px solid #e4e7ed;
  border-radius: 14px;
  font-size: 15px;
  line-height: 1.8;
  resize: none;
  padding: 16px;
  min-height: 120px;
  background: #fcfcfd;
}

.summary-input >>> .el-textarea__inner:focus {
  border-color: #e03426;
  box-shadow: 0 0 0 2px rgba(224, 52, 38, 0.1);
}

/* 富文本编辑器 */
.toolbar-group {
  display: flex;
  gap: 4px;
  align-items: center;
}

.toolbar-btn {
  width: 40px;
  height: 40px;
  border: 1px solid #e4e7ed;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  transition: all 0.2s ease;
}

.toolbar-btn-text {
  font-size: 14px;
  font-weight: 600;
}

.toolbar-btn:hover {
  background: #f5f5f5;
  border-color: #e03426;
  color: #e03426;
}

.toolbar-btn:disabled {
  background: #f5f7fa;
  border-color: #ebeef5;
  color: #c0c4cc;
  cursor: not-allowed;
  box-shadow: none;
}

.toolbar-btn.active {
  background: #e03426;
  color: white;
  border-color: #e03426;
}

.toolbar-text {
  font-size: 16px;
}

.toolbar-btn i {
  font-size: 16px;
}

.toolbar-btn strong,
.toolbar-btn em,
.toolbar-btn u {
  font-size: 16px;
  font-weight: bold;
}

.color-icon {
  background: linear-gradient(45deg, #ff0000, #00ff00, #0000ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.bg-icon {
  background: #ffff00;
  color: #333;
  padding: 2px 4px;
  border-radius: 2px;
}

.content-editor {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

.content-editor >>> .ql-container {
  border: none;
  font-size: 16px;
  line-height: 1.8;
  min-height: 520px;
}

.content-editor >>> .ql-editor {
  min-height: 520px;
  padding: 24px 28px 40px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.content-editor >>> .ql-editor.ql-blank::before {
  color: #c0c4cc;
  font-style: normal;
}

.content-editor >>> .ql-editor:focus {
  border-color: #e03426;
  box-shadow: 0 0 0 2px rgba(224, 52, 38, 0.1);
}

/* 图片调整样式 */
.content-editor >>> .ql-editor img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.content-editor >>> .ql-editor img:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* 图片尺寸类 */
.content-editor >>> .ql-editor img.image-adaptive {
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
  display: block !important;
  margin: 20px auto !important;
}

.content-editor >>> .ql-editor img.image-small {
  max-width: 200px;
  width: 200px;
  display: block;
  margin: 20px auto;
}

.content-editor >>> .ql-editor img.image-medium {
  max-width: 400px;
  width: 400px;
  display: block;
  margin: 20px auto;
}

.content-editor >>> .ql-editor img.image-large {
  max-width: 600px;
  width: 600px;
  display: block;
  margin: 20px auto;
}

.content-editor >>> .ql-editor img.image-full {
  max-width: 100% !important;
  width: 100% !important;
  height: auto !important;
  display: block !important;
  margin: 0 auto !important;
}

/* 图片工具栏样式 */
.image-toolbar {
  position: fixed;
  z-index: 9999;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e4e7ed;
  min-width: 280px;
}

.image-toolbar-content {
  padding: 16px;
}

.image-toolbar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
}

.image-toolbar-header span {
  font-weight: 600;
  color: #2c3e50;
}

.image-toolbar-header i {
  cursor: pointer;
  color: #999;
  font-size: 16px;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.image-toolbar-header i:hover {
  color: #e03426;
  background: #f5f5f5;
}

.image-toolbar-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.image-size-options {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.image-mode-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.mode-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #ebeef5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mode-option:hover {
  border-color: #e03426;
  background: rgba(224, 52, 38, 0.04);
}

.mode-option.active {
  border-color: #e03426;
  background: rgba(224, 52, 38, 0.08);
}

.mode-option span {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
}

.mode-option.active span {
  color: #e03426;
}

.mode-option-icon {
  flex-shrink: 0;
  border: 2px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
}

.mode-adaptive-icon {
  width: 30px;
  height: 20px;
}

.mode-fixed-icon {
  width: 18px;
  height: 20px;
}

.size-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.size-option:hover {
  background: #f5f5f5;
}

.size-option.active {
  background: rgba(224, 52, 38, 0.1);
  border-color: #e03426;
}

.size-option span {
  font-size: 12px;
  color: #666;
  text-align: center;
}

.size-option.active span {
  color: #e03426;
  font-weight: 500;
}

.size-option.disabled {
  opacity: 0.45;
}

.size-option.disabled:hover {
  background: transparent;
}

.size-preview {
  width: 40px;
  height: 30px;
  border: 2px solid #e4e7ed;
  border-radius: 4px;
  background: #f8f9fa;
}

.size-option.active .size-preview {
  border-color: #e03426;
}

.size-preview.small {
  width: 20px;
}

.size-preview.medium {
  width: 30px;
}

.size-preview.large {
  width: 35px;
}

.size-preview.full {
  width: 40px;
}

.image-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.content-editor >>> .ql-editor .ql-video {
  max-width: 100%;
  border-radius: 4px;
}

.preview-card {
  min-height: 720px;
  padding: 0;
  overflow: hidden;
}

.preview-article {
  min-height: 100%;
  background: #ffffff;
}

.preview-cover {
  width: 100%;
  height: 320px;
  overflow: hidden;
  background: #f2f3f5;
}

.preview-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-body {
  max-width: 820px;
  margin: 0 auto;
  padding: 40px 48px 56px;
}

.preview-category {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(224, 52, 38, 0.08);
  color: #e03426;
  font-size: 13px;
  font-weight: 600;
}

.preview-title {
  margin: 20px 0 12px;
  font-size: 40px;
  line-height: 1.25;
  font-weight: 700;
  color: #1f2329;
}

.preview-subtitle {
  margin: 0 0 16px;
  font-size: 18px;
  line-height: 1.6;
  color: #606266;
}

.preview-author-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: #909399;
  margin-bottom: 24px;
}

.preview-summary {
  margin: 0 0 28px;
  padding: 20px 24px;
  border-radius: 16px;
  background: #f7f8fa;
  font-size: 15px;
  line-height: 1.8;
  color: #606266;
}

.preview-content {
  border-top: 1px solid #f0f2f5;
  padding-top: 28px;
}

.preview-content >>> .ql-editor {
  padding: 0;
  min-height: 0;
  font-size: 17px;
  line-height: 1.9;
}

.preview-empty {
  min-height: 720px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: #909399;
}

.preview-empty i {
  font-size: 42px;
  color: #c0c4cc;
}

.editor-sidebar {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.sidebar-card {
  padding: 24px;
}

.sidebar-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2329;
}

.sidebar-desc {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: #909399;
}

.sidebar-section {
  margin-top: 20px;
}

.sidebar-checks {
  margin-top: 16px;
}

.sidebar-actions {
  display: flex;
  gap: 10px;
  margin-top: 24px;
}

.sidebar-actions .el-button {
  flex: 1;
}

.tag-editor {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.tag-item {
  margin-right: 0;
  margin-bottom: 0;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.stat-box {
  padding: 16px;
  background: #f7f8fa;
  border-radius: 16px;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2329;
}

.stat-label {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.quality-checks {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
}

.check-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #909399;
  font-size: 14px;
}

.check-item.done {
  color: #67c23a;
}

/* 发布对话框 */
.publish-form {
  padding: 16px 0;
}

.tag-input {
  width: 120px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.cover-cropper-dialog {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.cover-cropper-stage {
  display: flex;
  justify-content: center;
}

.cover-cropper-frame {
  position: relative;
  width: 100%;
  max-width: 720px;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 16px;
  background: #111827;
  cursor: grab;
  user-select: none;
}

.body-image-crop-frame {
  aspect-ratio: 16 / 10;
}

.cover-cropper-frame:active {
  cursor: grabbing;
}

.cover-cropper-image {
  position: absolute;
  max-width: none;
  max-height: none;
}

.cover-cropper-mask {
  position: absolute;
  inset: 0;
  border: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: inset 0 0 0 9999px rgba(17, 24, 39, 0.22);
  pointer-events: none;
}

.cover-cropper-controls {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cropper-control-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.cropper-control-row span {
  flex-shrink: 0;
  font-size: 14px;
  color: #606266;
}

.cropper-control-row .el-slider {
  flex: 1;
}

.cropper-control-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .editor-shell {
    grid-template-columns: 1fr;
  }

  .floating-image-btn {
    right: 24px;
  }
}

@media (max-width: 768px) {
  .editor-topbar {
    height: auto;
    padding: 16px;
    align-items: flex-start;
    flex-direction: column;
  }

  .topbar-center {
    justify-content: flex-start;
  }

  .editor-shell {
    padding: 16px;
    gap: 16px;
  }

  .workspace-header {
    padding-bottom: 6px;
  }

  .sticky-toolbar-card {
    gap: 12px;
    padding: 12px;
  }

  .toolbar-follow-tip,
  .toolbar-divider {
    display: none;
  }

  .editor-card,
  .sidebar-card {
    padding: 20px;
  }

  .editor-card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .title-input >>> .el-input__inner,
  .preview-title {
    font-size: 28px;
  }

  .preview-body {
    padding: 24px 20px 32px;
  }

  .preview-cover {
    height: 220px;
  }

  .content-editor >>> .ql-container,
  .content-editor >>> .ql-editor {
    min-height: 360px;
  }

  .sidebar-actions {
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }

  .floating-image-btn {
    right: 16px;
    bottom: 20px;
    padding: 12px 16px;
    font-size: 13px;
  }

  .cropper-control-row {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
}
</style> 